import config from '../config';
import Logger from '../logger';
import event from '../mixins/event';

class ThemeUtilityBar {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class constructor
	 */
	constructor(dom) {

		this.NAME = 'ThemeUtilityBar';

		this.dom = dom;
		this.weatherDom = this.dom.querySelector('[data-theme-utility-bar-weather]');
		this.weatherFetchInProgress = false; // Prevent duplicate API calls
	}

	/**
	 * init - Initialize the component
	 **/
	init() {
		Logger.info(`${this.NAME} > init`);
		this.scheduleWeatherFetch();
	}

	/**
	 * Defer WeatherAPI fetch until after window load + idle time so it is not
	 * chained on the critical path (Lighthouse "network dependency" / critical requests).
	 * Skip entirely when PHP rendered cached weather (data-theme-utility-bar-weather-ssr).
	 *
	 * @see https://www.weatherapi.com/docs/
	 */
	scheduleWeatherFetch() {
		if (!this.weatherDom) return;

		if (this.weatherDom.hasAttribute('data-theme-utility-bar-weather-ssr')) {
			Logger.info(`${this.NAME} > scheduleWeatherFetch: server-rendered weather, skipping client fetch`);
			return;
		}

		const run = () => this.initWeather();
		const start = () => {
			if (typeof window.requestIdleCallback === 'function') {
				window.requestIdleCallback(run, { timeout: 5000 });
			} else {
				window.setTimeout(run, 1);
			}
		};

		if (document.readyState === 'complete') {
			start();
		} else {
			window.addEventListener('load', start, { once: true });
		}
	}

	/**
	 * initWeather — client-side WeatherAPI fetch (fallback when SSR is not used).
	 */
	initWeather() {
		Logger.info(`${this.NAME} > initWeather`);

		if (!this.weatherDom) return;

		if (this.weatherDom.hasAttribute('data-theme-utility-bar-weather-ssr')) {
			return;
		}

		// Prevent duplicate API calls
		if (this.weatherFetchInProgress) {
			Logger.info(`${this.NAME} > initWeather: Already fetching, skipping duplicate call`);
			return;
		}

		const weatherApiKey = config.WEATHER_API_KEY;
		const weatherApiUrl = `https://api.weatherapi.com/v1/current.json?key=${weatherApiKey}&q=Denver,CO`;
		
		// Mark as in progress
		this.weatherFetchInProgress = true;
		
		// Add loading state
		this.weatherDom.classList.add('theme-utility-bar__weather--updating');
		
		fetch(weatherApiUrl)
			.then(response => response.json())
			.then(data => {
				// Fade out current content
				this.weatherDom.classList.add('theme-utility-bar__weather--fade-out');
				
				// Wait for fade-out to complete, then update and fade in
				setTimeout(() => {
					this.weatherDom.textContent = `${data.location.name}, ${data.location.region} ${data.current.temp_f.toFixed(0)}°F ${data.current.condition.text}`;
					
					// Remove fade-out, add fade-in
					this.weatherDom.classList.remove('theme-utility-bar__weather--fade-out');
					this.weatherDom.classList.add('theme-utility-bar__weather--fade-in');
					
					// Clean up classes after animation completes
					setTimeout(() => {
						this.weatherDom.classList.remove('theme-utility-bar__weather--updating', 'theme-utility-bar__weather--fade-in');
					}, 400);
				}, 200);
			})
			.catch(error => {
				console.error(error);
				// Remove loading state on error
				this.weatherDom.classList.remove('theme-utility-bar__weather--updating', 'theme-utility-bar__weather--fade-out');
			})
			.finally(() => {
				// Reset flag to allow future calls
				this.weatherFetchInProgress = false;
			});
	}

}

Object.assign(ThemeUtilityBar.prototype, event);

export default ThemeUtilityBar;
